import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Stack,
  Typography,
  Snackbar,
  Alert,
  Box,
  Tooltip
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { doc, updateDoc, arrayUnion, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { useUser } from '../../../../contexts/UserContext';
import { v4 as uuidv4 } from 'uuid';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  shareUrl: string;
  eventTitle: string;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  eventId,
  shareUrl,
  eventTitle
}) => {
  const [copied, setCopied] = useState(false);
  const { currentUser } = useUser();

  const trackShare = async (platform: 'facebook' | 'linkedin' | 'instagram' | 'direct') => {
    try {
      const eventRef = doc(db, 'events', eventId);
      const uniqueShareId = uuidv4();
      
      // Create the share URL with tracking parameters
      const trackedShareUrl = `${shareUrl}?sid=${uniqueShareId}&src=${platform}`;
      
      // Add share click to array with serverTimestamp for consistency
      await updateDoc(eventRef, {
        shareClicks: arrayUnion({
          id: `${Date.now()}-${platform}`,
          platform,
          timestamp: serverTimestamp(),
          referrer: window.location.href,
          userAgent: navigator.userAgent,
          sharedBy: currentUser ? {
            userId: currentUser.uid,
            displayName: currentUser.displayName || 'Anonymous',
            email: currentUser.email || 'unknown'
          } : undefined,
          uniqueShareId,
          clicks: 0,
          sales: 0,
          revenue: 0
        }),
        [`shareStats.${platform}`]: increment(1),
        'shareStats.total': increment(1),
        lastUpdated: serverTimestamp()
      });
      
      return trackedShareUrl;
    } catch (error) {
      console.error('Error tracking share:', error);
      return shareUrl;
    }
  };

  const handleCopyLink = async () => {
    try {
      const trackedUrl = await trackShare('direct');
      await navigator.clipboard.writeText(trackedUrl);
      setCopied(true);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handleSocialShare = async (platform: 'facebook' | 'linkedin' | 'instagram') => {
    try {
      const trackedUrl = await trackShare(platform);
      const encodedUrl = encodeURIComponent(trackedUrl);
      const encodedTitle = encodeURIComponent(eventTitle);
      let shareLink = '';
      
      switch (platform) {
        case 'facebook':
          shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;
        case 'linkedin':
          shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        case 'instagram':
          // Instagram sharing is typically done through their app/stories
          // For web, we'll copy the link and show instructions
          await navigator.clipboard.writeText(trackedUrl);
          alert('Link copied! Share this link in your Instagram bio or story.');
          return;
      }

      // Open share window
      if (shareLink) {
        window.open(shareLink, '_blank', 'width=600,height=400');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Share Event
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Share this event on social media or copy the link
          </Typography>
          
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Social Share Buttons */}
            <Stack direction="row" spacing={2} justifyContent="center">
              <Tooltip title="Share on Facebook">
                <IconButton
                  color="primary"
                  size="large"
                  onClick={() => handleSocialShare('facebook')}
                  sx={{ 
                    bgcolor: 'primary.light',
                    '&:hover': { bgcolor: 'primary.main', color: 'white' }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Share on LinkedIn">
                <IconButton
                  color="primary"
                  size="large"
                  onClick={() => handleSocialShare('linkedin')}
                  sx={{ 
                    bgcolor: 'primary.light',
                    '&:hover': { bgcolor: 'primary.main', color: 'white' }
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Share on Instagram">
                <IconButton
                  color="primary"
                  size="large"
                  onClick={() => handleSocialShare('instagram')}
                  sx={{ 
                    bgcolor: 'primary.light',
                    '&:hover': { bgcolor: 'primary.main', color: 'white' }
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Share Link */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                value={shareUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Button
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={handleCopyLink}
              >
                Copy
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareDialog; 